import type { AiCrawlerLineup } from './aiCrawlerLineup';

export type AiCrawlerProvider = 'ChatGPT' | 'Gemini' | 'Claude' | 'Perplexity' | 'Amazon';

export type AiCrawlerBot = {
  name: string;
  userAgent: string;
  purpose: string;
};

export type AiCrawler = {
  provider: AiCrawlerProvider;
  bots: AiCrawlerBot[];
};

export type MissingCrawlerDomain = {
  domain: string;
  missingCrawlers: AiCrawlerProvider[];
};

export function mapNormalizedToProvider(lineup: AiCrawlerLineup, normalized: string): AiCrawlerProvider | undefined {
  return lineup.normalizedToProvider.get(normalized.toLowerCase());
}

/**
 * Every normalized crawler id belonging to the same provider as the given id
 * (aliases and granular sub-labels included), or the id itself when it maps
 * to no tracked provider. The chart endpoints' `crawlers=` filter matches ids
 * verbatim in SQL, while error-rate responses key their points on one
 * pre-blended canonical id per provider (PRE-3145) — so a canonical response
 * key must be expanded back to its full id set before being sent as a filter,
 * or the provider's sub-label traffic silently drops out of the scope.
 */
export function expandProviderCrawlerIds(lineup: AiCrawlerLineup, normalized: string): string[] {
  const provider = mapNormalizedToProvider(lineup, normalized);
  if (provider === undefined) return [normalized];
  return Array.from(lineup.normalizedToProvider)
    .filter(([, candidate]) => candidate === provider)
    .map(([id]) => id);
}

export function mapBotNamesToProviders(lineup: AiCrawlerLineup, botNames: string[]): AiCrawlerProvider[] {
  const botToProvider = new Map<string, AiCrawlerProvider>();
  lineup.crawlers.forEach(({ provider, bots }) => {
    bots.forEach(({ name }) => {
      botToProvider.set(name, provider);
    });
  });

  const providers: AiCrawlerProvider[] = [];
  botNames.forEach((botName) => {
    const provider = botToProvider.get(botName);
    if (provider && !providers.includes(provider)) {
      providers.push(provider);
    }
  });

  return providers;
}

// Maps the providers flagged as missing on a domain to the crawler ids a
// diagnostics run is scoped to — deduplicated and in canonical provider order,
// so the scope stays stable regardless of the input order.
export function mapProvidersToDiagnosticsCrawlers(lineup: AiCrawlerLineup, providers: AiCrawlerProvider[]): string[] {
  return lineup.providers
    .filter((provider) => providers.includes(provider))
    .flatMap((provider) => lineup.providerToDiagnosticsCrawler.get(provider) ?? []);
}

// Formats a list of missing crawlers as a natural-language phrase:
//   []                       -> ''
//   [A]                      -> 'A'
//   [A, B]                   -> 'A and B'
//   [A, B, C]                -> 'A, B and C'
//   all tracked providers    -> 'All 4 AI Crawlers'
export function formatMissingCrawlers(lineup: AiCrawlerLineup, crawlers: AiCrawlerProvider[]): string {
  if (crawlers.length === 0) return '';
  if (crawlers.length === lineup.providers.length) return `All ${lineup.providers.length} AI Crawlers`;
  if (crawlers.length === 1) return crawlers[0];
  if (crawlers.length === 2) return `${crawlers[0]} and ${crawlers[1]}`;
  return `${crawlers.slice(0, -1).join(', ')} and ${crawlers[crawlers.length - 1]}`;
}

// Single source for the missing-crawlers info tooltip so the copy cannot
// drift between the Discoverability status banner and the Diagnostics
// missing-crawlers card (PRE-3137). Rendered as sanitized HTML by
// CustomTooltip, hence the <br /> line break.
export const MISSING_CRAWLERS_TOOLTIP =
  "Missing crawlers don't necessarily mean they're blocked. Some crawlers visit infrequently. Run diagnostics to confirm whether your site configuration is preventing AI Crawlers from accessing it." +
  '<br /><br />' +
  'Implemented the fix? Your missing crawlers update once they visit your site.';
